<script setup>
import LoadingSpinner from "@/Components/LoadingSpinner.vue";
import {computed, ref} from 'vue';
import { QuestionMarkCircleIcon } from "@heroicons/vue/solid";
import {ElOption, ElSelect, ElTooltip} from 'element-plus';
import helper from "@/helper";
import {usePage} from "@inertiajs/inertia-vue3";


const props = defineProps({
    leadCategory: {required: true, type: String,},
    formData: Object,
    leadSource: Object,
    leadSourceNotifications: Object,
    leadTypes: Array,
    actionSchedules: Array,
    startingPoints: Array,
    agents: Array,
    buckets: Array,
    referralProgramUsers: {},
    sourceOwnerEmail: {
        type: String,
        default: null
    },
    errors: {
        type: Object,
        default: null
    }
})

const roles = usePage().props.value?.auth?.user?.roles.map(role => role.name);
const isAnyAdmin = roles.includes('Super Admin') || roles.includes('Agency Admin');

const emit = defineEmits(['submit']);

const modelName = computed(() => {
  switch (props.leadCategory) {
    case 'recruitment':
      return 'Recruitment Source';
    case 'commercial':
    default:
      return 'Lead Source';
  }
});
const isCommercialLead = computed(() => { // Commercial Lead: classic lead. The other type is a recruitment lead
  return !props.leadCategory || props.leadCategory !== 'recruitment';
});
// Shared AI visibility map (HandleInertiaRequests). Selecting Smart mode is allowed
// either way — this only drives the "not enabled yet" warning.
const smartNextActionAvailable = computed(() => usePage().props.value?.features?.['smart-next-step'] === true);

const starting_point_label = ref('');
const show_starting_point_select = ref(false);
const startingPointValue = ref([]);

function isInvalid(attribute) {
    if (props.formData.errors && props.formData.errors[attribute]) {
        return true;
    } else {
        return props.errors && props.errors.hasOwnProperty(attribute);
    }
}

function errorMessage(attribute) {
    if (props.formData.errors && props.formData.errors[attribute]) {
        return props.formData.errors[attribute];
    } else if (props.errors && props.errors.hasOwnProperty(attribute)) {
        return props.errors[attribute][0];
    }
}

// Duplicate notification emails management
const duplicateEmailToAdd = ref('');
const duplicateEmailError = ref(null);

function addDuplicateNotificationEmail() {
    const email = duplicateEmailToAdd.value?.trim();
    if (!email) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        duplicateEmailError.value = 'Please enter a valid email address.';
        return;
    }

    // Initialize array if needed
    if (!props.formData.duplicate_notification_emails) {
        props.formData.duplicate_notification_emails = [];
    }

    // Check for duplicates
    if (props.formData.duplicate_notification_emails.includes(email)) {
        duplicateEmailError.value = 'This email is already added.';
        return;
    }

    props.formData.duplicate_notification_emails.push(email);
    duplicateEmailToAdd.value = '';
    duplicateEmailError.value = null;
}

function removeDuplicateNotificationEmail(email) {
    const index = props.formData.duplicate_notification_emails.findIndex(e => e === email);
    if (index > -1) {
        props.formData.duplicate_notification_emails.splice(index, 1);
    }
}

function prefillSourceOwnerEmail() {
    if (props.sourceOwnerEmail &&
        (!props.formData.duplicate_notification_emails || props.formData.duplicate_notification_emails.length === 0)) {
        props.formData.duplicate_notification_emails = [props.sourceOwnerEmail];
    }
}

</script>

<template>
    <div>
        <form @submit.prevent="emit('submit')" class="intro-y box p-5 bg-white lead-source-form " method="POST">
            <div class="grid gap-6 grid-cols-1 mt-3">
                <!-- BEGIN: Vendor -->
                <div class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('vendor')}">
                    <label for="vendor" class="form-label text-sm  text-gray-800">Vendor</label>
                    <input v-model="formData.vendor" id="vendor" type="text" class="form-control input-field w-full" name="vendor">
                    <div v-if="isInvalid('vendor')" class="error-message mt-1">{{ errorMessage('vendor') }}</div>
                </div>
                <!-- END: Vendor -->

                <!-- BEGIN: Label -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('label')}">
                <div class="flex text-tb-blue">
                    <label for="label" class="form-label text-sm mt-2 text-gray-800">Label</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">Source name that sub agents would see.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                    <input v-model="formData.label" id="label" type="text" class="form-control input-field w-1/2" name="label">
                    <div v-if="isInvalid('label')" class="error-message mt-1">{{ errorMessage('label') }}</div>
                    <input v-model="formData.label_searchable_to_sub_agents" value="1" id="label_searchable_to_sub_agents" type="checkbox" class="input-checkbox form-check-input mr-2" name="label_searchable_to_sub_agents">
                    <label for="label_searchable_to_sub_agents" class="form-label text-sm  text-gray-800">Searchable to Sub Agents</label>
                    <div v-if="isInvalid('label_searchable_to_sub_agents')" class="error-message mt-1">{{ errorMessage('label_searchable_to_sub_agents') }}</div>
                </div>
                <!-- END: Label -->

                <!-- BEGIN: Lead Type -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('lead_type_id')}">
                    <label for="lead_type_id" class="form-label text-sm  text-gray-800">Lead Type</label>
                    <select v-model="formData.lead_type_id" id="lead_type_id" class="form-control input-field w-full" name="lead_type_id">
                                                <option>- Select -</option>
                                                <option v-for="leadType in leadTypes" :value="leadType.id">{{ leadType.name }}</option>
                                            </select>
                    <div v-if="isInvalid('lead_type_id')" class="error-message mt-1">{{ errorMessage('lead_type_id') }}</div>
                </div>
                <!-- END: Lead Type -->

                <!-- BEGIN: Cost Per Lead -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('cost_per_lead')}">
                    <div class="flex text-tb-blue">
                    <label for="cost_per_lead" class="form-label mt-2 text-sm text-gray-800">Cost Per Lead</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">How much you pay for each lead.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                    <input v-model="formData.cost_per_lead" id="cost_per_lead" type="text" class="form-control input-field w-full" name="cost_per_lead">
                    <div v-if="isInvalid('cost_per_lead')" class="error-message mt-1">{{ errorMessage('cost_per_lead') }}</div>
                </div>
                <!-- END: Cost Per Lead -->

                <!-- BEGIN: Notify When Dead -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('notify_agency_when_dead')}">
                    <div class="flex text-tb-blue">
                    <label for="notify_agency_when_dead" class="form-label mt-2 text-sm text-gray-800">Notify Admin When Lead Has a Dead Status</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">If selected, an email will be sent to the Agency Admin once<br> a lead from this source is changed to dead status.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                    <select v-model="formData.notify_agency_when_dead" id="notify_agency_when_dead" class="form-control input-field w-full" name="notify_agency_when_dead">
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                    </select>
                    <div v-if="isInvalid('notify_agency_when_dead')" class="error-message mt-1">{{ errorMessage('notify_agency_when_dead') }}</div>
                </div>
                <!-- END: Notify When Dead -->

                <!-- BEGIN: Next Action Mode -->
                <div class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('next_action_mode')}">
                    <div class="flex text-tb-blue">
                    <label for="next_action_mode" class="form-label mt-2 text-sm text-gray-800">Next Actions Driven By</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">Action Schedule: leads follow the schedule's configured steps.<br>Smart Next Action: AI suggests each next action, templates and timing<br>on the Next Action page instead of following a schedule.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                    <select v-model="formData.next_action_mode" id="next_action_mode" class="form-control input-field w-full" name="next_action_mode">
                        <option value="schedule">Action Schedule</option>
                        <option value="smart">Smart Next Action (AI)</option>
                    </select>
                    <div v-if="formData.next_action_mode === 'smart' && !smartNextActionAvailable" class="mt-1 text-sm text-amber-600">
                        AI isn't enabled for your agency yet — leads from this source will use the free-form next action until it is.
                    </div>
                    <div v-if="isInvalid('next_action_mode')" class="error-message mt-1">{{ errorMessage('next_action_mode') }}</div>
                </div>
                <!-- END: Next Action Mode -->

                <!-- BEGIN: Action Schedule -->
                <div v-if="formData.next_action_mode !== 'smart'" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('action_schedule_id')}">
                    <div class="flex text-tb-blue">
                    <label for="action_schedule_id" class="form-label mt-2 text-sm text-gray-800">Action Schedule</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">Choose the action schedule that this lead should follow.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                    <select v-model="formData.action_schedule_id" id="action_schedule_id" class="form-control input-field w-full" name="action_schedule_id">
                        <option :value="null">Select</option>
                        <option v-for="scheduleMap in actionSchedules" :value="scheduleMap.id">{{ scheduleMap.name }}</option>
                    </select>
                    <div v-if="isInvalid('action_schedule_id')" class="error-message mt-1">{{ errorMessage('action_schedule_id') }}</div>
                    <template v-if="isCommercialLead && isAnyAdmin">
                        <div class="flex items-center mt-2">
                            <input v-model="formData.allow_agent_action_schedules" id="allow_agent_action_schedules" type="checkbox" class="input-checkbox form-check-input mr-2" name="allow_agent_action_schedules">
                            <label for="allow_agent_action_schedules" class="form-label text-sm text-gray-800">Allow agents to use their own action schedules</label>
                        </div>
                        <p class="text-xs text-gray-500">Agents pick one of their own schedules for this source under Action Schedules. It applies to leads they get after choosing.</p>
                        <div v-if="isInvalid('allow_agent_action_schedules')" class="error-message mt-1">{{ errorMessage('allow_agent_action_schedules') }}</div>
                    </template>
                </div>
                <!-- END: Action Schedule -->

                <!-- BEGIN: Starting Point -->
                <div class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('starting_point')}">
                    <div class="flex text-tb-blue">
                    <label for="starting_point" class="form-label mt-2 text-sm text-gray-800">Starting Point</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">Choose how this lead should be<br> assigned once it is created in the system.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                    <select v-model="formData.starting_point" id="starting_point" class="form-control input-field w-full" name="starting_point">
                        <option v-for="startingPoint in startingPoints" :value="startingPoint.id">{{ startingPoint.name }}</option>
                    </select>
                    <div v-if="isInvalid('starting_point')" class="error-message mt-1">{{ errorMessage('starting_point') }}</div>
                </div>
                <!-- END: Starting Point -->

            <!-- BEGIN: Representing Agency -->
              <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('representing_agency')}">
                <div class="flex text-tb-blue">
                  <label for="representing_agency" class="form-label mt-2 text-sm text-gray-800">Representing Agency</label>
                </div>
                <input v-model="formData.representing_agency" id="representing_agency" type="text" class="form-control input-field w-full" name="representing_agency">
                <div v-if="isInvalid('representing_agency')" class="error-message mt-1">{{ errorMessage('representing_agency') }}</div>
            </div>
            <!-- END: Representing Agency -->

            <!-- BEGIN: Hard Time Limit -->
              <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('hard_time_limit')}">
                <div class="flex text-tb-blue">
                <label for="hard_time_limit" class="form-label mt-2 text-sm text-gray-800">Hard Time Limit</label>
                <el-tooltip placement="top" >
                    <template #content>
                        <p class="text-sm">You can optionally set a certain amount of hours<br> that the first owning agent can have the lead.<br> If the lead is still in uncontacted status after the<br> number of hours specified, it will be pulled<br> and assigned accordingly.</p>
                        </template>
                        <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                    </el-tooltip>
                </div>
                <input v-model="formData.hard_time_limit" id="hard_time_limit" type="text" class="form-control input-field w-full" name="hard_time_limit">
                <div v-if="isInvalid('hard_time_limit')" class="error-message mt-1">{{ errorMessage('hard_time_limit') }}</div>
            </div>
            <!-- END: Hard Time Limit -->

            <!-- BEGIN: Bad Phone Action -->
            <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('hard_time_limit_action')}">
                <div class="flex text-tb-blue">
                <label for="hard_time_limit_action" class="form-label text-sm mt-2 text-gray-800">Hard Time Limit Action</label>
                <el-tooltip placement="top" >
                        <template #content>
                            <p class="text-sm">What happens to the lead once the Hard Time Limit is Met</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                </div>
                <select v-model="formData.hard_time_limit_action" id="hard_time_limit_action" class="form-control input-field w-full" name="hard_time_limit_action">
                    <option>Select</option>
                    <option :value="null">No action</option>
                    <option value="1">Transfer to agent</option>
                    <option value="2">Transfer to bucket</option>
                </select>
                <div v-if="isInvalid('hard_time_limit_action')" class="error-message mt-1">{{ errorMessage('hard_time_limit_action') }}</div>
            <!-- END: Bad Phone Action -->
                <!-- BEGIN: Bad Phone Action -->
                <div v-if="isCommercialLead && formData.hard_time_limit_action == 1" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('htl_user_id')}">
                    <label for="htl_user_id" class="form-label text-sm  text-gray-800">Agent</label>
                    <select v-model="formData.htl_user_id" id="htl_user_id" class="form-control input-field w-full" name="htl_user_id">
                        <option :value="null">Select Agent</option>
                        <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.last_name }}, {{ agent.first_name }}</option>
                    </select>
                    <div v-if="isInvalid('htl_user_id')" class="error-message mt-1">{{ errorMessage('htl_user_id') }}</div>
                </div>
                <!-- END: Bad Phone Action -->
                <!-- BEGIN: Bad Phone Action -->
                <div v-if="isCommercialLead && formData.hard_time_limit_action == 2" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('htl_bucket_id')}">
                    <label for="htl_bucket_id" class="form-label text-sm  text-gray-800">Bucket</label>
                    <select v-model="formData.htl_bucket_id" id="htl_bucket_id" class="form-control input-field w-full" name="htl_bucket_id">
                        <option :value="null">Select Bucket</option>
                        <option v-for="bucket in buckets" :key="bucket.id" :value="bucket.id">{{ bucket.name }}</option>
                    </select>
                    <div v-if="isInvalid('htl_bucket_id')" class="error-message mt-1">{{ errorMessage('htl_bucket_id') }}</div>
                </div>
            </div>

            <!-- BEGIN: Weekly Dispo -->
            <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('weekly_dispo_email')}">
                <div class="flex text-tb-blue">
                <label for="weekly_dispo_email" class="form-label mt-2 text-sm text-gray-800">Weekly Dispo File Email</label>
                <el-tooltip placement="top" >
                    <template #content>
                        <p class="text-sm">You can send your lead vendor a disposition detail<br> file every Saturday morning by putting their email address here.</p>
                        </template>
                        <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                    </el-tooltip>
                </div>
                <input v-model="formData.weekly_dispo_email" id="weekly_dispo_email" type="text" class="form-control input-field w-full" name="weekly_dispo_email">
                <div v-if="isInvalid('weekly_dispo_email')" class="error-message mt-1">{{ errorMessage('weekly_dispo_email') }}</div>
            </div>
            <!-- END: Weekly Dispo -->

            <!-- BEGIN: Sale Notification -->
            <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('sale_notification_emails')}">
                <div class="flex text-tb-blue">
                <label for="sale_notification_emails" class="form-label mt-2 text-sm text-gray-800">Sale Notification Email</label>
                </div>
                <input v-model="formData.sale_notification_emails" id="sale_notification_emails" type="text" class="form-control input-field w-full" name="sale_notification_emails">
                <div v-if="isInvalid('sale_notification_emails')" class="error-message mt-1">{{ errorMessage('sale_notification_emails') }}</div>
            </div>
            <!-- END: Sale Notification -->

            <!-- BEGIN: Send Dispo -->
            <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('send_dispo_type')}">
                <div class="flex text-tb-blue">
                <label for="send_dispo_type" class="form-label mt-2 text-sm text-gray-800">Send Dispo Type</label>
                <el-tooltip placement="top" >
                    <template #content>
                        <p class="text-sm">Weekly will be sent on Saturday at 8am for the previous week Sat-Fri.<br>Daily will be sent each day for the previous day at 8am</p>
                        </template>
                        <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                    </el-tooltip>
                </div>       
                <select v-model="formData.send_dispo_type" id="send_dispo_type" class="form-control input-field w-full" name="send_dispo_type">
                    <option :value="null">Select</option>
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                </select>                
                    <div v-if="isInvalid('send_dispo_type')" class="error-message mt-1">{{ errorMessage('send_dispo_type') }}</div>
            </div>
            <!-- END: Send Dispo -->

                <!-- BEGIN: Give Bad Phone Credit -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form w-full" :class="{'has-error': isInvalid('give_bad_phone_credit')}">
                    <div class="flex text-tb-blue">
                    <label for="give_bad_phone_credit" class="form-label mt-2 text-sm text-gray-800">Return Disconnected Number Credits</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">If selected, the agent will recieve a credit in agency bucks for what the agent paid.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                        <select v-model="formData.give_bad_phone_credit" id="give_bad_phone_credit" class="form-control input-field w-full" name="give_bad_phone_credit">
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                        </select>
                    <div v-if="isInvalid('give_bad_phone_credit')" class="error-message mt-1">{{ errorMessage('give_bad_phone_credit') }}</div>
                </div>
              <!-- END: Give Bad Phone Credit -->

                <!-- BEGIN: Time To Allow Bad Phone Credit -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form w-full" :class="{'has-error': isInvalid('timeframe_bad_phone_credit')}">
                    <div class="flex text-tb-blue">
                    <label for="timeframe_bad_phone_credit" class="form-label mt-2 text-sm text-gray-800">Time To Allow Disconnected Number Credit</label>
                    <el-tooltip placement="top" >
                            <template #content>
                            <p class="text-sm">If selected, only give credit within this timeline.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                        <select v-model="formData.timeframe_bad_phone_credit" id="timeframe_bad_phone_credit" class="form-control input-field w-full" name="timeframe_bad_phone_credit">
                            <option value="1440">24 Hours</option>
                            <option value="2880">48 Hours</option>
                            <option value="4320">72 Hours</option>
                            <option value="5760">4 days</option>
                        </select>
                    <div v-if="isInvalid('timeframe_bad_phone_credit')" class="error-message mt-1">{{ errorMessage('timeframe_bad_phone_credit') }}</div>
                </div>
                <!-- END: Time To Allow Bad Phone Credit -->

                <!-- BEGIN: Bad Phone Action -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('bad_phone_action')}">
                    <label for="bad_phone_action" class="form-label text-sm  text-gray-800">Disconnected Number Action</label>
                    <select v-model="formData.bad_phone_action" id="bad_phone_action" class="form-control input-field w-full" name="bad_phone_action">
                        <option :value="null">No action</option>
                        <option value="1">Transfer to agent</option>
                        <option value="2">Transfer to bucket</option>
                    </select>
                    <div v-if="isInvalid('bad_phone_action')" class="error-message mt-1">{{ errorMessage('bad_phone_action') }}</div>
                    <!-- BEGIN: Bad Phone Action -->
                    <div v-if="formData.bad_phone_action == 1" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('bad_phone_action_user_id')}">
                        <label for="bad_phone_action_user_id" class="form-label text-sm  text-gray-800">Agent</label>
                        <select v-model="formData.bad_phone_action_user_id" id="bad_phone_action_user_id" class="form-control input-field w-full" name="bad_phone_action_user_id">
                            <option :value="null">Select Agent</option>
                            <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.last_name }}, {{ agent.first_name }}</option>
                        </select>
                        <div v-if="isInvalid('bad_phone_action_user_id')" class="error-message mt-1">{{ errorMessage('bad_phone_action_user_id') }}</div>
                    </div>
                    <!-- END: Bad Phone Action -->
                    <!-- BEGIN: Bad Phone Action -->
                    <div v-if="formData.bad_phone_action == 2" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('bad_phone_action_bucket_id')}">
                        <label for="bad_phone_action_bucket_id" class="form-label text-sm  text-gray-800">Bucket</label>
                        <select v-model="formData.bad_phone_action_bucket_id" id="bad_phone_bucket_id" class="form-control input-field w-full" name="bad_phone_action_bucket_id">
                            <option :value="null">Select Bucket</option>
                            <option v-for="bucket in buckets" :key="bucket.id" :value="bucket.id">{{ bucket.name }}</option>
                        </select>
                        <div v-if="isInvalid('bad_phone_action_bucket_id')" class="error-message mt-1">{{ errorMessage('bad_phone_action_bucket_id') }}</div>
                    </div>
                </div>
                <!-- END: Bad Phone Action -->
                <!-- BEGIN: Dead Action -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('dead_action')}">
                    <label for="dead_action" class="form-label text-sm  text-gray-800">Dead Action</label>
                    <select v-model="formData.dead_action" id="dead_action" class="form-control input-field w-full" name="dead_action">
                        <option :value="null">No action</option>
                        <option value="1">Transfer to agent</option>
                        <option value="2">Transfer to bucket</option>
                    </select>
                    <div v-if="isInvalid('dead_action')" class="error-message mt-1">{{ errorMessage('dead_action') }}</div>
                    <!-- END: Dead Action -->
                    <div v-if="formData.dead_action == 1" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('dead_action_user_id')}">
                        <label for="dead_action_user_id" class="form-label text-sm  text-gray-800">Agent</label>
                        <select v-model="formData.dead_action_user_id" id="dead_action_user_id" class="form-control input-field w-full" name="dead_action_user_id">
                            <option :value="null">Select Agent</option>
                            <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.last_name }}, {{ agent.first_name }}</option>
                        </select>
                        <div v-if="isInvalid('dead_action_user_id')" class="error-message mt-1">{{ errorMessage('dead_action_user_id') }}</div>
                    </div>
                    <!-- END: Bad Phone Action -->
                    <div v-if="formData.dead_action == 2" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('dead_action_bucket_id')}">
                        <label for="dead_action_bucket_id" class="form-label text-sm  text-gray-800">Bucket</label>
                        <select v-model="formData.dead_action_bucket_id" id="dead_action_bucket_id" class="form-control input-field w-full" name="dead_action_bucket_id">
                            <option :value="null">Select Bucket</option>
                            <option v-for="bucket in buckets" :key="bucket.id" :value="bucket.id">{{ bucket.name }}</option>
                        </select>
                        <div v-if="isInvalid('dead_action_bucket_id')" class="error-message mt-1">{{ errorMessage('dead_action_bucket_id') }}</div>
                    </div>
                </div>
                <!-- BEGIN: Give Already Purchased Credit -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('give_already_purchased_credit')}">
                    <div class="flex text-tb-blue">
                        <label for="give_already_purchased_credit" class="form-label text-sm mt-2 text-gray-800">Give Already Purchased Credit?</label>
                        <el-tooltip placement="top" >
                            <template #content>
                                <p class="text-sm">If selected, the agent will recieve a credit in agency bucks for what the agent paid.</p>
                            </template>
                            <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                        </el-tooltip>
                    </div>
                    <select v-model="formData.give_already_purchased_credit" id="give_already_purchased_credit" class="form-control input-field w-full" name="give_already_purchased_credit">
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                    </select>
                    <div v-if="isInvalid('give_already_purchased_credit')" class="error-message mt-1">{{ errorMessage('give_already_purchased_credit') }}</div>
                </div>
                <!-- END: Give Already Purchased Credit -->
                <!-- BEGIN: Time To Allow Already Purchased Credit -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form w-full" :class="{'has-error': isInvalid('timeframe_already_purchased_credit')}">
                  <div class="flex text-tb-blue">
                    <label for="timeframe_already_purchased_credit" class="form-label mt-2 text-sm text-gray-800">Time To Allow Already Purchased Credit</label>
                    <el-tooltip placement="top" >
                      <template #content>
                        <p class="text-sm">If selected, only give credit within this timeline.</p>
                      </template>
                      <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                    </el-tooltip>
                  </div>
                  <select v-model="formData.timeframe_already_purchased_credit" id="timeframe_already_purchased_credit" class="form-control input-field w-full" name="timeframe_already_purchased_credit">
                    <option value="1440">24 Hours</option>
                    <option value="2880">48 Hours</option>
                    <option value="4320">72 Hours</option>
                    <option value="5760">4 days</option>
                  </select>
                  <div v-if="isInvalid('timeframe_already_purchased_credit')" class="error-message mt-1">{{ errorMessage('timeframe_already_purchased_credit') }}</div>
                </div>
                <!-- END: Time To Allow Already Purchased Credit -->
                <!-- BEGIN: Active -->
                <div class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('active')}">
                    <label for="active" class="form-label text-sm  text-gray-800">Active</label>
                    <select v-model="formData.active" id="active" class="form-control input-field w-full" name="active">
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                    </select>
                    <div v-if="isInvalid('active')" class="error-message mt-1">{{ errorMessage('active') }}</div>
                </div>
                <!-- END: Active -->

                <!-- BEGIN: RTB Daily -->
                <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form mt-2" :class="{'has-error': isInvalid('rtb_daily_overflow_cap')}">
                    <label for="rtb_daily_overflow_cap" class="form-label mt-2 text-sm text-gray-800 whitespace-nowrap">Turn on RTB?</label>
                    <input v-model="formData.rtb_active" id="rtb_daily_overflow_cap" type="checkbox" class="input-field h-4 cursor-pointer w-4 mt-1 rounded border-gray-300 text-tb-blue focus:ring-tb-blue" name="rtb_daily_overflow_cap">
                    <div v-if="isInvalid('rtb_daily_overflow_cap')" class="error-message mt-1">{{ errorMessage('rtb_daily_overflow_cap') }}</div>
                </div>
                <!-- END: RTB Daily -->

                <!-- BEGIN: RTB Daily -->
                <div v-if="isCommercialLead && formData.rtb_active == 1" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('rtb_daily_overflow_cap')}">
                    <label for="rtb_daily_overflow_cap" class="form-label mt-2 text-sm text-gray-800">RTB Daily Overflow Cap</label>
                    <input v-model="formData.rtb_daily_overflow_cap" id="rtb_daily_overflow_cap" type="text" class="form-control input-field w-full" name="rtb_daily_overflow_cap">
                    <div v-if="isInvalid('rtb_daily_overflow_cap')" class="error-message mt-1">{{ errorMessage('rtb_daily_overflow_cap') }}</div>
                </div>
                <!-- END: RTB Daily -->

                <!-- BEGIN: RTB Weekly -->
                <div v-if="isCommercialLead && formData.rtb_active == 1" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('rtb_weekly_overflow_cap')}">
                    <label for="rtb_weekly_overflow_cap" class="form-label mt-2 text-sm text-gray-800">RTB Weekly Overflow Cap</label>
                    <input v-model="formData.rtb_weekly_overflow_cap" id="rtb_weekly_overflow_cap" type="text" class="form-control input-field w-full" name="rtb_weekly_overflow_cap">
                    <div v-if="isInvalid('rtb_weekly_overflow_cap')" class="error-message mt-1">{{ errorMessage('rtb_weekly_overflow_cap') }}</div>
                </div>
                <!-- RTB Weekly -->



              <!-- BEGIN: Notify On Duplicate -->
              <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('notify_duplicates')}">
                <div class="flex text-tb-blue">
                  <label for="notify_duplicates" class="form-label mt-2 text-sm text-gray-800">Notify Agent on Duplicate API Leads</label>
                  <el-tooltip placement="top" >
                    <template #content>
                      <p class="text-sm">If enabled, the source owner will receive an email when an incoming API lead matches an existing lead by email or phone.</p>
                    </template>
                    <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                  </el-tooltip>
                </div>
                <select v-model="formData.notify_duplicates" id="notify_duplicates" class="form-control input-field w-full" name="notify_duplicates">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
                <div v-if="isInvalid('notify_duplicates')" class="error-message mt-1">{{ errorMessage('notify_duplicates') }}</div>
              </div>
              <!-- END: Notify On Duplicate -->

              <!-- BEGIN: Duplicate Notification Emails -->
              <div v-if="isCommercialLead && formData.notify_duplicates == 1" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('duplicate_notification_emails')}">
                <div class="flex text-tb-blue">
                  <label for="duplicate_notification_emails" class="form-label mt-2 text-sm text-gray-800">Notification Emails</label>
                  <el-tooltip placement="top">
                    <template #content>
                      <p class="text-sm">Enter the email addresses that should receive duplicate lead notifications. If left empty, the source owner's email will be used.</p>
                    </template>
                    <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                  </el-tooltip>
                </div>
                <!-- Email tags display -->
                <div class="flex items-center justify-start space-x-2 flex-wrap" v-if="formData.duplicate_notification_emails && formData.duplicate_notification_emails.length">
                  <div v-for="(email, i) in formData.duplicate_notification_emails" :key="email" class="bg-slate-300 mt-1 pl-3 pr-2 py-0.5 rounded-md text-sm flex justify-between items-center space-x-2 mr-1">
                    <span class="text-sm py-1 font-medium">{{ email }}</span>
                    <button @click.prevent="removeDuplicateNotificationEmail(email)" class="btn text-red-500 p-0 rounded-md border-none">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    </button>
                  </div>
                </div>
                <!-- Email input -->
                <div class="mt-2">
                  <input
                    v-model="duplicateEmailToAdd"
                    @focus="prefillSourceOwnerEmail"
                    id="duplicate_notification_emails"
                    :placeholder="sourceOwnerEmail ? sourceOwnerEmail : 'example@email.com'"
                    type="email"
                    class="form-control form-control-sm input-field w-full"
                    name="duplicate_notification_emails"
                    @keydown.enter.prevent="addDuplicateNotificationEmail"
                  >
                  <button @click.prevent="addDuplicateNotificationEmail" type="button" class="btn-primary text-xs mt-2">Add</button>
                </div>
                <div v-if="duplicateEmailError" class="error-message mt-1">{{ duplicateEmailError }}</div>
                <div v-if="isInvalid('duplicate_notification_emails')" class="error-message mt-1">{{ errorMessage('duplicate_notification_emails') }}</div>
              </div>
              <!-- END: Duplicate Notification Emails -->

              <!-- BEGIN: 24 Hour Duplicate Check -->
              <div v-if="isCommercialLead" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('enable_24hr_duplicate_check')}">
                <div class="flex text-tb-blue">
                  <label for="enable_24hr_duplicate_check" class="form-label mt-2 text-sm text-gray-800">24 Hour Duplicate Check</label>
                  <el-tooltip placement="top" >
                    <template #content>
                      <p class="text-sm">If enabled, incoming API leads will be rejected if a duplicate email or phone number was submitted within the past 24 hours by the same source.</p>
                    </template>
                    <QuestionMarkCircleIcon @click.prevent="true" class="p-1 ml-1 h-8 w-8 cursor-pointer"></QuestionMarkCircleIcon>
                  </el-tooltip>
                </div>
                <select v-model="formData.enable_24hr_duplicate_check" id="enable_24hr_duplicate_check" class="form-control input-field w-full" name="enable_24hr_duplicate_check">
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
                <div v-if="isInvalid('enable_24hr_duplicate_check')" class="error-message mt-1">{{ errorMessage('enable_24hr_duplicate_check') }}</div>
              </div>
              <!-- END: 24 Hour Duplicate Check -->

              <!-- BEGIN: Show to Other Agents on Lead Create -->
              <div v-if="isCommercialLead && isAnyAdmin" class="col-span-12 lg:col-span-6 input-form" :class="{'has-error': isInvalid('show_to_other_agents')}">
                <div class="flex text-tb-blue">
                  <label for="show_to_other_agents" class="form-label mt-2 text-sm text-gray-800">Show to Other Agents on Lead Create</label>
                </div>
                <select v-model="formData.show_to_other_agents" id="show_to_other_agents" class="form-control input-field w-full" name="show_to_other_agents">
                  <option :value="1">Yes</option>
                  <option :value="0">No</option>
                </select>
                <div v-if="isInvalid('show_to_other_agents')" class="error-message mt-1">{{ errorMessage('show_to_other_agents') }}</div>
              </div>
              <!-- END: Show to Other Agents on Lead Create -->

              <!-- BEGIN: Referral Program Users -->
              <div v-if="isAnyAdmin" class="col-span-12 lg:col-span-6 input-form" :class="{ 'has-error': isInvalid('referral_program_user_ids') }">
                <label for="leader_ids" class="text-gray-600 font-bold">Referral User Access</label>
                <el-select v-model="formData.referral_program_user_ids" id="referral_program_user_ids" class="input-field w-full" multiple clearable>
                  <el-option v-for="(user, i) in referralProgramUsers" :key="user.id" :label="`${user.last_name}, ${user.first_name}`" :value="user.id" />
                </el-select>
                <div v-if="isInvalid('referral_program_user_ids')" class="error-message mt-1">{{ errorMessage('referral_program_user_ids') }}</div>
              </div>
              <!-- END: Referral Program Users -->
            </div>

            <div class="text-right mt-5">
                <button type="submit" class="btn btn-primary w-24" :class="{'pointer-events-none': loading}">
                    <LoadingSpinner v-if="loading"></LoadingSpinner>
                    <span v-else>Submit</span>
                </button>
            </div>
        </form>
    </div>
</template>
